import { doDelete, doGet2, doPost2WithFormData } from './common'
import { runAdmin, toApiError } from './builderDrafts'

/**
 * The media library (Beaker2 images, `/admin/image`) as the Puck editor uses it
 * (BR-5691): browsing and searching, uploading and deleting.
 *
 * Calls go through `runAdmin`, so they never overlap with the editor's own admin calls
 * (see runQueued in builderDrafts).
 */

/**
 * Editor images live under /puck/ (uploads, and /puck/ai/ for generated ones), so the
 * editor's library pickers always search there instead of offering a folder field.
 */
export const PUCK_LIBRARY_FOLDER = '/puck/'

export const MEDIA_PATH = '/admin/image'

export const MEDIA_LIBRARY_PAGE_SIZE = 24

/**
 * An `Image` document as `/admin/image` returns it. Field values store this object
 * unchanged, whether it came from an upload or from the library, so renderers
 * (`getPuckFileUrl` reads `link`) see one shape.
 */
export type MediaImage = {
  id: string
  filename: string
  link: string
  path: string
  name?: string | null
  altAttribute?: string | null
  library?: string | null
  bucket?: string | null
  imageType?: string | null
  source?: string | null
  width?: number | null
  height?: number | null
  _created?: string | null
  size?: number | null
  contentType?: string | null
  originalImageId?: string | null
}

export type MediaLibraryQuery = {
  /** Matched against the name or the file name. */
  search?: string
  /** Path prefix, e.g. `/puck/`. */
  folder?: string
  page?: number
  size?: number
}

export type MediaLibraryPage = {
  /** Newest first (the API orders them). */
  images: MediaImage[]
  page: number
  hasMore: boolean
  total: number
}

export type ImageSize = { width: number; height: number }

export const EMPTY_UPLOAD_MESSAGE =
  'Upload failed: the server returned no image'

/**
 * Query parameters for `GET /admin/image`. Blank filters are left out rather than sent
 * empty, and nothing is ever `undefined`: the query encoder would send the string
 * "undefined".
 */
export const buildLibraryParams = ({
  search,
  folder,
  page = 0,
  size = MEDIA_LIBRARY_PAGE_SIZE,
}: MediaLibraryQuery = {}): Record<string, string | number> => {
  const params: Record<string, string | number> = {}
  const term = search?.trim()
  if (term) params.search = term
  const prefix = folder?.trim()
  if (prefix) params.folder = prefix
  params.page = page
  params.size = size
  return params
}

/** Only entries with a link can be shown or stored. */
const hasLink = (value: unknown): value is MediaImage =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as MediaImage).link === 'string' &&
  (value as MediaImage).link !== ''

type SpringPage = {
  content?: unknown[]
  number?: number
  totalPages?: number
  totalElements?: number
  last?: boolean
}

/**
 * How long the picker waits for a page of the library. The API answers a 500 only
 * after its own ~30 s Mongo timeout, and the request holds the editor's admin queue
 * meanwhile (runQueued), so a save would wait behind it.
 */
export const MEDIA_LIBRARY_TIMEOUT_MS = 15_000

export const MEDIA_LIBRARY_TIMEOUT_MESSAGE =
  'The media library did not respond. Try again.'

export type FetchLibraryOptions = {
  /** Cancels the request (the picker closed, or a newer search replaced it). */
  signal?: AbortSignal
  /** Defaults to MEDIA_LIBRARY_TIMEOUT_MS. */
  timeoutMs?: number
}

const ABORT_ERROR = 'AbortError'

/** Rejected with when the caller cancelled: nothing to show, nobody is waiting. */
const abortError = () => {
  const error = new Error('The media library request was cancelled')
  error.name = ABORT_ERROR
  return error
}

/** The request was cancelled by its caller (not a timeout, not a failure). */
export const isAbortError = (error: unknown): boolean =>
  !!error && (error as Error).name === ABORT_ERROR

/**
 * One page of the library, newest first.
 *
 * Gives up after `timeoutMs` with MEDIA_LIBRARY_TIMEOUT_MESSAGE, also when the request
 * is still waiting in the admin queue: the fetch is aborted, which frees the queue. A
 * network failure reads the same. Cancelling through `signal` rejects with an abort
 * error (isAbortError) and sends nothing if the request had not started.
 */
export const fetchMediaLibrary = (
  query: MediaLibraryQuery = {},
  { signal, timeoutMs = MEDIA_LIBRARY_TIMEOUT_MS }: FetchLibraryOptions = {},
): Promise<MediaLibraryPage> => {
  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)
  const cancel = () => controller.abort()
  if (signal?.aborted) controller.abort()
  else signal?.addEventListener('abort', cancel)

  const failure = () =>
    timedOut ? new Error(MEDIA_LIBRARY_TIMEOUT_MESSAGE) : abortError()

  // Settles as soon as the request is given up, even while it is still queued.
  const givenUp = new Promise<never>((_, reject) => {
    if (controller.signal.aborted) reject(failure())
    else controller.signal.addEventListener('abort', () => reject(failure()))
  })

  const request = runAdmin(async () => {
    if (controller.signal.aborted) throw failure()
    try {
      const response = await doGet2(MEDIA_PATH, buildLibraryParams(query), {
        signal: controller.signal,
      })
      if (!response.ok) {
        throw await toApiError(response, 'Failed to load the media library')
      }
      const text = await response.text()
      const body: SpringPage = text ? JSON.parse(text) : {}
      const page = body.number ?? query.page ?? 0
      const hasMore =
        typeof body.last === 'boolean'
          ? !body.last
          : page + 1 < (body.totalPages ?? 0)
      return {
        images: (body.content ?? []).filter(hasLink),
        page,
        hasMore,
        total: body.totalElements ?? 0,
      }
    } catch (error) {
      if (controller.signal.aborted) throw failure()
      // fetch rejects with a TypeError when the API cannot be reached at all.
      if (error instanceof TypeError) {
        throw new Error(MEDIA_LIBRARY_TIMEOUT_MESSAGE)
      }
      throw error
    }
  })

  return Promise.race([request, givenUp]).finally(() => {
    clearTimeout(timer)
    signal?.removeEventListener('abort', cancel)
  })
}

/**
 * The uploaded `Image`. The API used to answer 200 with an empty body when the storage
 * upload failed, which surfaced as the browser's "Unexpected end of JSON input"; a body
 * without a link is just as unusable.
 */
const readUploadedImage = async (response: Response): Promise<MediaImage> => {
  if (!response.ok) throw await toApiError(response, 'Upload failed')
  const text = await response.text()
  let image: unknown = null
  try {
    image = text.trim() ? JSON.parse(text) : null
  } catch {
    // Not JSON: treated like an empty answer below.
  }
  if (!hasLink(image)) throw new Error(EMPTY_UPLOAD_MESSAGE)
  return image
}

export type UploadOptions = {
  /** Folder in the bucket, e.g. `/puck/`. */
  path: string
  /** Measured in the browser; the API cannot read every format (WebP, SVG). */
  size?: ImageSize | null
}

/** Uploads a file to the library and returns its `Image`. */
export const uploadMediaFile = (
  file: File,
  { path, size }: UploadOptions,
): Promise<MediaImage> => {
  const payload: Record<string, string | number | boolean | File> = {
    file,
    path,
    renameFile: true,
    // Unique in the bucket; `name` keeps the original for the library list and search.
    fileName: `${Date.now()}-${file.name}`,
    name: file.name,
  }
  if (size) {
    payload.width = size.width
    payload.height = size.height
  }
  return runAdmin(async () =>
    readUploadedImage(await doPost2WithFormData(MEDIA_PATH, payload)),
  )
}

/** Deletes a file from storage. A 404 means it is already gone, which is the goal. */
export const deleteMediaFile = (id: string): Promise<void> =>
  runAdmin(async () => {
    const response = await doDelete(`${MEDIA_PATH}/${id}`)
    if (!response.ok && response.status !== 404) {
      throw await toApiError(response, 'Delete failed')
    }
  })

const MEASURE_TIMEOUT_MS = 3000

const validSize = (width: number, height: number): ImageSize | null =>
  width > 0 && height > 0 ? { width, height } : null

const measureWithImageElement = (
  file: Blob,
  timeoutMs: number,
): Promise<ImageSize | null> => {
  if (typeof URL.createObjectURL !== 'function') return Promise.resolve(null)
  return new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    const finish = (size: ImageSize | null) => {
      clearTimeout(timer)
      img.onload = null
      img.onerror = null
      URL.revokeObjectURL(url)
      resolve(size)
    }
    const timer = setTimeout(() => finish(null), timeoutMs)
    img.onload = () => finish(validSize(img.naturalWidth, img.naturalHeight))
    img.onerror = () => finish(null)
    img.src = url
  })
}

/**
 * Width and height of an image file, best effort: null for anything that is not an
 * image or cannot be decoded. Never rejects, so a failed measurement never blocks the
 * upload. `createImageBitmap` cannot decode SVG, which the `<img>` fallback can.
 */
export const measureImageFile = async (
  file: Blob,
  timeoutMs = MEASURE_TIMEOUT_MS,
): Promise<ImageSize | null> => {
  if (!file.type.startsWith('image/')) return null
  if (
    typeof createImageBitmap === 'function' &&
    file.type !== 'image/svg+xml'
  ) {
    try {
      const bitmap = await createImageBitmap(file)
      const size = validSize(bitmap.width, bitmap.height)
      bitmap.close?.()
      if (size) return size
    } catch {
      // Undecodable here; the <img> element may still manage.
    }
  }
  return measureWithImageElement(file, timeoutMs)
}
